import { useCallback } from 'react';
import { PaymentMutations } from '@frontend/api-payments';
import { HttpError } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';

type UseStripeDashboardLinkOptions = {
  locationId: string | undefined;
};

export interface PaymentProcessorResponse {
  activated: boolean;
  id: string;
  links: {
    connectionToken: null;
    dashboard: string | null;
    location: string;
    paymentLocation: string;
    paymentMethods?: string;
    paymentRequest: string;
    paymentTerminal?: string;
  };
  locationId: string;
  stripeUserId: string;
  terminal: {
    locationId: string;
  };
}

export type PaymentProcessorDataResponse = {
  data: PaymentProcessorResponse;
};
export const useStripeDashboardLink = ({ locationId }: UseStripeDashboardLinkOptions) => {
  const alert = useAlert();
  const t = useTranslation('payments').t;

  const { getDashboardLink, isGettingDashboardLink, dashboardLinkError } =
    PaymentMutations.useStripeDashboardUrlMutation(locationId);

  const handleOpenStripeDashboard = useCallback(async () => {
    if (isGettingDashboardLink) {
      console.error('Already loading dashboard link - target should not be clickable');
      return;
    }
    try {
      const data = await getDashboardLink();

      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        alert.error(t('Failed to generate Stripe dashboard login link for this location'));
      }
    } catch (error) {
      if (error instanceof HttpError) {
        if (error.status === 400) {
          alert.error(t('Invalid location ID'));
        } else if (error.status === 403) {
          alert.error(t('You do not have permission to access the dashboard'));
        } else {
          alert.error(t('Failed to generate Stripe dashboard login link for this location'));
        }
      } else if (error) {
        console.warn('Unexpected error type', error);
        alert.error(t('Failed to generate Stripe dashboard login link for this location'));
      }
    }
  }, [isGettingDashboardLink, getDashboardLink]);

  return {
    handleOpenStripeDashboard,
    isGettingDashboardLink,
    dashboardLinkError,
  };
};
